'use client';
import NextTopLoader from 'nextjs-toploader';
import style from './layout-wrapper.module.scss';
import Header from '@/components/common/header';
import Footer from '@/components/common/footer';
import clsx from 'clsx';
import Sidebar from '@/components/common/sidebar';
import { useSidebar } from '@/context/SidebarContext';
import { usePathname } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { sidebarData } from '@/lib/utils/sidebar';
import NotFound from '@/app/not-found';
import { useSocket } from '@/hooks/useSocket';
import { notificationMsg } from '@/components/common/toastify-msg';
import { setAlertData } from '@/lib/features/notification/notificationSlice';
import { generateAuth, generateUrlSearchParams } from '@/lib/utils/axios';
import { setOnlineUsers } from '@/lib/features/online-users/onlineUsersSlice';

const LayoutWrapper = ({ children }) => {
    const { isSidebarOpen } = useSidebar();
    const pathname = usePathname();
    const isLoginPage = pathname?.endsWith('/login');
    const router = useRouter();
    const { isLoggedIn, loading } = useSelector((state) => state.session);
    const { remoteId } = useSelector((state) => state.session);
    const [permission, setPermission] = useState({});
    const [permissionFetched, setPermissionFetched] = useState(false);
    const [isAllowed, setIsAllowed] = useState(false);
    const { userId, username, accountType } = useSelector((state) => state.session);
    const { socket } = useSocket();
    const dispatch = useDispatch();

    const handleAlert = useCallback((alert) => {
        const alertItem = {
            id: Date.now() + Math.random(),
            message: alert,
            timestamp: new Date().toLocaleTimeString(),
        };
        dispatch(setAlertData(alertItem));
        notificationMsg(alert);
    }, []);

    useEffect(() => {
        if (!isLoggedIn || !userId) return;
        if (!socket) {
            return;
        }

        socket.on('alert', handleAlert);

        socket.emit('join-room', `${username}_${userId}_${accountType}`);
        return () => {
            socket.off('alert', handleAlert);
            socket.off('notification');
        };
    }, [socket, isLoggedIn, username, userId, accountType]);

    useEffect(() => {
        if (!userId && !loading) {
            router.push('/login');
        }
    }, [userId, loading]);


    useEffect(() => {
        if (!remoteId) return;
        const fetchPermissions = async () => {
            try {
                const params = {
                    remoteId,
                    casino: process.env.NEXT_PUBLIC_CASINO,
                };
                const res = await axios.get(`${process.env.NEXT_PUBLIC_API_DOMAIN}/api/players/get-permission`, { params });
                setPermission(res.data?.permission);
            } catch (err) {
                console.error('Permission fetch failed:', err);
            } finally {
                setPermissionFetched(true);
            }
        };
        fetchPermissions();
    }, [remoteId]);

    const getFilteredSidebarData = (permissions) => {
        const allSidebarItems = sidebarData();
        const permissionMap = {
            provider_management: 'provider-management',
            game_management: 'game-management',
            provider_skin_limits: 'provider-skin-limit',
            general_bet_limits: 'general-bet-limit',
            bet_limits_parameters: 'bet-limits-parameters',
            win_limits_parameters: 'win-limits-parameters',
        };
        return allSidebarItems.filter((item) => {
            if (!permissionMap[item.key]) return true;
            const permissionKey = permissionMap[item.key];
            return permissions?.[permissionKey] === true;
        });
    };
    const sidebarLinksData = useMemo(() => {
        return permission ? getFilteredSidebarData(permission) : [];
    }, [permission]);

    useEffect(() => {
        // if (!permissionFetched || sidebarLinksData.length === 0 || !pathname) return;
        const currentBasePath = pathname?.split('/')[1];
        const sidebarPaths = sidebarLinksData.map((link) => link.href);

        if (pathname == '/' || pathname?.includes('/notification-alert')) {
            setIsAllowed(true);
        } else {
            const isAllowed = sidebarPaths.some((path) => currentBasePath === path?.split('/')[1]);
            setIsAllowed(isAllowed);
        }
    }, [permissionFetched, pathname, sidebarLinksData]);

    const handleUpdateIP = async (ip) => {
        const authKey = generateAuth(`remoteId=${remoteId}&action=update-ip-mac`);
        const params = generateUrlSearchParams({
            token: process.env.NEXT_PUBLIC_TOKEN,
            authKey,
            casino: process.env.NEXT_PUBLIC_CASINO,
            AUTH_ID: remoteId,
        });
        const payload = {
            action: 'update-ip-mac',
            remoteId: remoteId,
            ...(ip && { userIP: ip }),
        };
        try {
            const data = await axios.post(`${process.env.NEXT_PUBLIC_API_DOMAIN}/players/update-user?${params}`, payload);
        } catch {}
    };

    useEffect(() => {
        if (!remoteId) return;
        (async () => {
            try {
                const res = await fetch('https://api.ipify.org?format=json');
                if (!res.ok) throw new Error('Failed to fetch IP');
                const { ip } = await res.json();
                if (ip) await handleUpdateIP(ip);
            } catch (err) {
                console.error('Error fetching client IP:', err);
            }
        })();
    }, [remoteId]);

    useEffect(() => {
        if (socket && userId) {
            socket.emit('presence:connect', {
                parentId: null,
                userId,
                username,
                role: accountType,
            });

            const handleOnlineUsers = (players) => {
                console.log(players, 'player :: admin');
                dispatch(setOnlineUsers(players));
            };
            socket.on('online-users', handleOnlineUsers);

            return () => {
                socket.off('presence:connect');
            };
        }
    }, [socket, userId]);
    return (
        children && (
            <>
                <NextTopLoader
                    color="#FFE69E"
                    initialPosition={0.08}
                    crawlSpeed={300}
                    height={2}
                    crawl={true}
                    showSpinner={false}
                    easing="ease"
                    speed={100}
                    shadow="0 0 .625rem #FFE69E ,0 0 .3125rem #FFE69E"
                    zIndex={1600}
                    showAtBottom={false}
                />
                {isLoginPage ? (
                    children
                ) : (
                    <div className={clsx(style.layout_wp, clsx(isSidebarOpen && style.sidebar_close), 'layout_wp')}>
                        {isAllowed ? (
                            <>
                                <Header />
                                <Sidebar sidebarLinksData={sidebarLinksData} />
                                <div className={style.content}>{children}</div>
                                <Footer />
                            </>
                        ) : (
                            <NotFound />
                        )}
                    </div>
                )}
            </>
        )
    );
};
export default LayoutWrapper;
